import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Lightbulb, Projector, Bot } from 'lucide-react';
import HolidaySelector from '@/components/displays/HolidaySelector';

interface BasicInfoSectionProps {
  name: string;
  description: string;
  displayType: string;
  holidays: string[];
  lightCount: number | null;
  projectorUsed: boolean;
  illumibotUsed: boolean;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onDisplayTypeChange: (value: string) => void;
  onHolidaysChange: (value: string[]) => void;
  onLightCountChange: (value: number | null) => void;
  onProjectorUsedChange: (value: boolean) => void;
  onIllumibotUsedChange: (value: boolean) => void;
}

const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  name,
  description,
  displayType,
  holidays,
  lightCount,
  projectorUsed,
  illumibotUsed,
  onNameChange,
  onDescriptionChange,
  onDisplayTypeChange,
  onHolidaysChange,
  onLightCountChange,
  onProjectorUsedChange,
  onIllumibotUsedChange,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Basic Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="display-name">Display Name *</Label>
          <Input
            id="display-name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Enter your display name"
            required
          />
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Tell visitors about your display..."
            rows={4}
          />
        </div>

        <div>
          <Label htmlFor="display-type">Display Type</Label>
          <Select value={displayType} onValueChange={onDisplayTypeChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select display type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Residential">Residential</SelectItem>
              <SelectItem value="Commercial">Commercial</SelectItem>
              <SelectItem value="Community">Community</SelectItem>
              <SelectItem value="Church">Church</SelectItem>
              <SelectItem value="School">School</SelectItem>
              <SelectItem value="Projection Show">Projection Show</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="light-count" className="flex items-center gap-1">
            <Lightbulb className="h-4 w-4" />
            Number of Lights (Optional)
          </Label>
          <Input
            id="light-count"
            type="number"
            min={0}
            max={9999999}
            value={lightCount || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                onLightCountChange(null);
              } else {
                const numValue = parseInt(value, 10);
                if (!isNaN(numValue) && numValue >= 0 && numValue <= 9999999) {
                  onLightCountChange(numValue);
                }
              }
            }}
            placeholder="e.g., 50000"
          />
        </div>

        {displayType === 'Projection Show' && (
          <>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="projector-used"
                checked={projectorUsed}
                onCheckedChange={onProjectorUsedChange}
              />
              <Label htmlFor="projector-used" className="flex items-center gap-1">
                <Projector className="h-4 w-4" />
                Projector Used
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="illumibot-used"
                checked={illumibotUsed}
                onCheckedChange={onIllumibotUsedChange}
              />
              <Label htmlFor="illumibot-used" className="flex items-center gap-1">
                <Bot className="h-4 w-4" />
                Illumibot Used
              </Label>
            </div>
          </>
        )}

        <div>
          <Label>Holidays</Label>
          <div className="mt-2">
            <HolidaySelector
              value={holidays}
              onChange={onHolidaysChange}
              mode="badge"
              layout="grid"
              showIcons={true}
            />
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Select all holidays your display celebrates
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default BasicInfoSection;